import React, { useState, useEffect } from 'react';
import { useController } from '@rest-hooks/react';
import { useHistory, useLocation} from "react-router-dom";
import PropTypes from 'prop-types';

import AuthenticationResource from 'resources/auth/AuthenticationResource';
import TokenExchangeResource from 'resources/onboarding/TokenExchangeResource';

import loginValidation from './Validations/loginForm';

import StyledTextField from 'ui/StyledComponents/StyledTextField';

import Button from 'ui/Button';
import FormSection from 'ui/FormSection';
import Label from 'ui/text/Label';

import CoursesList from 'utils/localStorage/CoursesList';
import { useError } from 'utils/useErrorController';
import redirectToView from 'utils/redirectToView';
import { checkLoginStatus } from 'utils/checkLoginStatus';

import { t } from 'i18n/index';
import handleSuccessfulAuth from 'utils/handleSuccessfulAuth';
import { Link } from '@mui/material';

const LogInForm = ({done, onLoginChange=null}) => {
  
  const { fetch } = useController();
  const history = useHistory();
  const location = useLocation();

  const isLoggedIn = checkLoginStatus();

  const searchParams = new URLSearchParams(location.search);
  const transferTokenParam = searchParams.get('transferToken');
  const redirectToParam = searchParams.get('redirectTo');

  const fromState = location.state && location.state.from ? location.state.from : null;
  const targetView = redirectToParam || fromState;

  const {loading, setLoading, validate, handleError} = useError();
  
  const [data, setData] = useState({});

  useEffect(() => {   
    if (isLoggedIn) {
      redirectToView(history, 'student', CoursesList.read(), targetView);
    } else if (transferTokenParam) {
      handleTransferTokenExchange(transferTokenParam, searchParams);
    }
  }, []);

  const removeTokenFromUrl = (searchParams) => {
    searchParams.delete('transferToken');
    searchParams.delete('redirectTo');
    const newSearch = searchParams.toString();
    const newUrl = location.pathname + (newSearch ? `?${newSearch}` : '');
    history.replace(newUrl);
  };

  const handleTransferTokenExchange = async (transferToken, searchParams) => {
    try {
      setLoading(true);
      const { auth_token, user, courses } = await fetch(
        TokenExchangeResource.create(), 
        { transfer_token: transferToken }
      );

      handleSuccessfulAuth(auth_token, user, courses);
      
      removeTokenFromUrl(searchParams);
      
      if (done) {
        done();
      } else {
        redirectToView(history, user.role, courses, targetView);
      }
      setLoading(false);
    } catch (error) {
      setLoading(false);
      removeTokenFromUrl(searchParams);
      handleError(error);
    }
  };

  const handleChange = ({ target: {name, value}}) => {
    setData( {
      ...data,
      [name]: value
    });
  };

  const handleEmailLogin = async () => {
    try {
      validate(data, loginValidation);

      const formData = new FormData();
      Object.entries(data).forEach(([name, value]) => formData.append(name, value));

      await handleLogin(data);
    } catch (error) {
      handleError(error);
    }
  };

  const handleLogin = async (data) => {
    const { auth_token, user, courses } = await fetch(AuthenticationResource.create(), data );

    if (auth_token){
      handleSuccessfulAuth(auth_token, user, courses);

      if (done){
        done()
      } else {
        redirectToView(history, user.role, courses, targetView);
      }
      setLoading(false);
    }
  };

  return (
    <FormSection slim >
      <StyledTextField
        label={t('literal:email')}
        name="email"
        value={data.email || ''}
        onChange={ handleChange }
        fullWidth>
      </StyledTextField>

      <StyledTextField
        label={t('literal:password')}
        name="password"
        type={'password'}
        value={data.password || ''}
        onChange={ handleChange }
        fullWidth>
      </StyledTextField>

      <Label secondary padded>
        {t('authForms:loginForm:forgotPasswordQuestion')}&nbsp;
        <Link href="/password_resets" target="_blank" underline="hover" color="inherit" >
          {t('authForms:loginForm:forgotPasswordLink')}
        </Link><br/>
          {onLoginChange &&
            <>
            {t('authForms:loginForm:newToMembrzQuestion')}
            <Link href="#" onClick={ onLoginChange } underline="hover" color="inherit" >
              {t('authForms:loginForm:signUpCall')}
            </Link>
          </>
          }
      </Label>

      <Button highlighted fullwidth loading={loading} onClick={ handleEmailLogin }>
        {t('literal:signIn')}
      </Button>
    </FormSection> 
  );
};

LogInForm.propTypes = {
  done: PropTypes.func,
  onLoginChange: PropTypes.func
};

export default LogInForm;
